import { filter } from "lodash";
import { notifyError, notifySuccess } from "@linkfluence/core/notifications";
import { Locale } from "@linkfluence/models/locale";
import { useLocalStorage } from "@lkf/hooks";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { isPinnedAuthorCard } from "@radarly/models/cards/is";
import { Clause } from "@radarly/models/clause";
import { getClauseFromCard } from "@radarly/models/clause/getter";
import { getCopiedDataKey } from "@radarly/models/insight-pages/editor";
import { Project } from "@radarly/models/project";
import { Workspace } from "@radarly/models/workspace";

type PageCopiedData = {
  cards: Card[];
  filters: { cardTitle: string; clause: Clause }[];
};

const toastContent = {
  filters_copied: {
    success: {
      title: "INSIGHT_PAGES_EDITOR_FILTERS_COPIED_OK",
      description: "INSIGHT_PAGES_EDITOR_FILTERS_COPIED_OK_TEXT",
    },
    error: {
      title: "INSIGHT_PAGES_EDITOR_FILTERS_COPIED_ERROR",
      description: "INSIGHT_PAGES_EDITOR_FILTERS_COPIED_ERROR_TEXT",
    },
  },
  filters_pasted: {
    success: {
      title: "INSIGHT_PAGES_EDITOR_FILTERS_PASTED_OK",
      description: "INSIGHT_PAGES_EDITOR_FILTERS_PASTED_OK_TEXT",
    },
    error: {
      title: "INSIGHT_PAGES_EDITOR_FILTERS_PASTED_ERROR",
      description: "INSIGHT_PAGES_EDITOR_FILTERS_PASTED_ERROR_TEXT",
    },
  },
  card_copied: {
    success: {
      title: "INSIGHT_PAGES_EDITOR_CARD_COPIED_OK",
      description: "INSIGHT_PAGES_EDITOR_CARD_COPIED_OK_TEXT",
    },
    error: {
      title: "INSIGHT_PAGES_EDITOR_CARD_COPIED_ERROR",
      description: "INSIGHT_PAGES_EDITOR_CARD_COPIED_ERROR_TEXT",
    },
  },
  card_pasted: {
    success: {
      title: "INSIGHT_PAGES_EDITOR_CARD_PASTED_OK",
      description: "INSIGHT_PAGES_EDITOR_CARD_PASTED_OK_TEXT",
    },
    error: {
      title: "INSIGHT_PAGES_EDITOR_CARD_PASTED_ERROR",
      description: "INSIGHT_PAGES_EDITOR_CARD_PASTED_ERROR_TEXT",
    },
  },
};

export default function useCopyPasteEditor(
  locale: Locale,
  projectId: Project["id"],
  dashbordId?: Workspace["id"]
) {
  const pageCopiedDataKey = getCopiedDataKey(projectId, dashbordId);
  const [value, setValue] = useLocalStorage<PageCopiedData>(pageCopiedDataKey, {
    cards: [],
    filters: [],
  });

  const generateToast = (
    outcome: "error" | "success",
    type: keyof typeof toastContent
  ) => {
    const message = toastContent[type][outcome];
    if (outcome === "success") {
      return notifySuccess(
        tr(locale, message.title),
        tr(locale, message.description)
      );
    }
    return notifyError(
      tr(locale, message.title),
      tr(locale, message.description)
    );
  };

  const copyFilters = (card: Card) => {
    try {
      const clause = getClauseFromCard(card);
      const visibleFilters = [...value.filters];
      if (visibleFilters.length === 3) {
        visibleFilters.shift();
      }
      visibleFilters.push({ cardTitle: card.title, clause });
      setValue({ ...value, filters: visibleFilters });
      generateToast("success", "filters_copied");
    } catch (_) {
      generateToast("error", "filters_copied");
    }
  };

  const copyCard = (card: Card) => {
    try {
      const visibleCards = [...value.cards];
      if (visibleCards.length === 3) {
        visibleCards.shift();
      }
      visibleCards.push(card);
      setValue({ ...value, cards: visibleCards });
      generateToast("success", "card_copied");
    } catch (_) {
      generateToast("error", "card_copied");
    }
  };

  const isCardFiltered = (card: Card): boolean => {
    if (isPinnedAuthorCard(card)) {
      return false;
    }
    const clause = getClauseFromCard(card);
    const filteredClause = filter(clause.and, (filterClause) => {
      const field = Object.keys(filterClause).toString();
      return field !== "date" && field !== "dashboard";
    });
    return filteredClause.length > 0;
  };

  return {
    copyFilters,
    isCardFiltered,
    copyCard,
  };
}
